import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Flower2, X, Sparkles, ArrowRight } from 'lucide-react';
import { useGardenTime } from '../../context/GardenTimeContext';
import { applyBloomMonth, restoreBloom } from '../../utils/bloomMode';
import { aggregateBloomCalendar } from '../../utils/bloomUtils';
import { GARDEN_OBJECT_TYPES } from '../../constants/gardenObjectTypes';
import BloomCalendar from './BloomCalendar';

/**
 * BloomPanel - Panel-Inhalt „Blüte" (#608/#609, Andocken #633)
 *
 * Selbsttragender Panel-Inhalt ohne Positions-Wissen: Desktop rendert ihn in
 * der rechten Seitenleiste (PropertiesPanel-Slot), Tablet/Mobile im
 * FloatingViewPanel. Gemountet = offen — die Blüh-Einfärbung läuft, solange
 * die Monatsansicht aktiv ist, und stellt beim Unmount/Umschalten exakt den
 * Originalzustand wieder her (bloomMode, strikt nicht-destruktiv).
 *
 * Der Monat kommt aus dem geteilten GardenTimeContext (#608) — „mein Garten
 * im Juni" ist hier, im Schatten-Panel und in 3D derselbe Juni.
 */

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const MONTH_SHORT = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const BloomPanel = ({ canvas, onClose, onFindPlantsForMonth }) => {
  const { month, setMonth } = useGardenTime();
  const [mode, setMode] = useState('month'); // 'month' | 'year'
  const [canvasVersion, setCanvasVersion] = useState(0);

  const colorActive = !!canvas && mode === 'month';

  // Struktur-Änderungen am Canvas verfolgen — treibt sowohl das Neu-Einfärben
  // (Monatsansicht) als auch den Kalender.
  useEffect(() => {
    if (!canvas) return undefined;
    const bump = () => setCanvasVersion((v) => v + 1);
    canvas.on('object:added', bump);
    canvas.on('object:removed', bump);
    return () => {
      canvas.off('object:added', bump);
      canvas.off('object:removed', bump);
    };
  }, [canvas]);

  // Blüh-Einfärbung nur in der Monatsansicht; beim Monatswechsel, beim
  // Platzieren/Löschen und beim Unmount/Umschalten exakt zurück ins Original.
  useEffect(() => {
    if (!colorActive) return undefined;
    applyBloomMonth(canvas, month);
    return () => restoreBloom(canvas);
  }, [colorActive, canvas, month, canvasVersion]);

  // Blühkalender aus den Pflanzen des Canvas.
  const calendar = useMemo(() => {
    if (!canvas) return null;
    const plants = canvas
      .getObjects()
      .filter((o) => o.objectType === GARDEN_OBJECT_TYPES.PLANT);
    return aggregateBloomCalendar(plants);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canvasVersion triggert Neuaufbau
  }, [canvas, canvasVersion]);

  const handleSelectMonth = (m) => {
    setMonth(m);
    setMode('month');
  };

  // Echte Blühlücke (#662): der Garten blüht irgendwann im Jahr, im gewählten
  // Monat aber gerade nicht. Nur dann ein leiser Entdeckungs-Hinweis — bei
  // leerem/blütenlosem Garten bleibt die Fläche ruhig, kein Nagging. Der Hinweis
  // erscheint nur, wenn ein Katalog-Einstieg gereicht wurde (Desktop/Tablet).
  const isBloomGap =
    !!onFindPlantsForMonth &&
    !!calendar &&
    calendar[month]?.count === 0 &&
    calendar.some((c) => c.count > 0);

  return (
    <div>
      <div className="flex items-center gap-2">
        <Flower2 className="h-4 w-4 text-pink-500" aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-800">Blüte im Jahresverlauf</span>
        <button
          type="button"
          aria-label="Schließen"
          onClick={onClose}
          className="ml-auto rounded-md p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Ansicht-Schalter */}
      <div
        role="group"
        aria-label="Ansicht"
        className="mt-2 inline-flex rounded-md border border-gray-200 p-0.5 text-xs"
      >
        {[
          { id: 'month', label: 'Monatsansicht' },
          { id: 'year', label: 'Jahresübersicht' },
        ].map((m) => (
          <button
            key={m.id}
            type="button"
            aria-pressed={mode === m.id}
            onClick={() => setMode(m.id)}
            className={`rounded px-2 py-1 font-medium transition-colors ${
              mode === m.id ? 'bg-pink-50 text-pink-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'month' ? (
        <>
          {/* Monats-Slider */}
          <div className="mt-3">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-500">Monat</span>
              <span className="text-sm font-semibold text-gray-900">{MONTH_NAMES[month]}</span>
            </div>
            <input
              type="range"
              min={0}
              max={11}
              step={1}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              aria-label="Monat"
              aria-valuetext={MONTH_NAMES[month]}
              className="mt-1 w-full accent-pink-500"
            />
            <div className="mt-0.5 flex justify-between text-[10px] text-gray-400">
              {MONTH_SHORT.map((m, i) => (
                <span key={i} aria-hidden="true">
                  {m}
                </span>
              ))}
            </div>
          </div>

          {/* Legende */}
          <ul className="mt-3 space-y-1 text-xs text-gray-600">
            <li className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full border border-pink-300 bg-pink-400"
                aria-hidden="true"
              />
              blüht in diesem Monat
            </li>
            <li className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full border border-gray-300 bg-gray-200 opacity-55"
                aria-hidden="true"
              />
              ruht gerade
            </li>
            <li className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full border border-gray-300 bg-white"
                aria-hidden="true"
              />
              ohne Blühangabe
            </li>
          </ul>

          {/* Blühlücken-Hinweis (#662): ruhiger Vorschlag, kein Druck. Führt in
              den Katalog, vorgefiltert auf den Monat — Entdeckung, kein To-do. */}
          {isBloomGap && (
            <div className="mt-3 rounded-md bg-gray-50 p-3">
              <p className="text-xs leading-relaxed text-gray-600">
                Im {MONTH_NAMES[month]} blüht in deinem Garten gerade nichts.
              </p>
              <button
                type="button"
                onClick={() => onFindPlantsForMonth(month)}
                className="mt-1 inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
              >
                Pflanzen für {MONTH_NAMES[month]} entdecken
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-3">
          {calendar && (
            <BloomCalendar
              calendar={calendar}
              currentMonth={month}
              onSelectMonth={handleSelectMonth}
            />
          )}
          <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
            Farbtupfer zeigen, welche Arten wann blühen. Leere Felder sind ruhige
            Monate. Tippe auf einen Monat, um ihn im Garten zu sehen.
          </p>
        </div>
      )}

      <p className="mt-3 flex items-center gap-1 text-xs text-emerald-700">
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        Premium-Vorschau — frei ausprobieren, dein Plan bleibt unberührt.
      </p>
    </div>
  );
};

BloomPanel.propTypes = {
  canvas: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  // Öffnet den Katalog vorgefiltert auf einen Monat (0-basiert). Optional —
  // fehlt im read-only-Mobile-Editor, dann bleibt der Blühlücken-Hinweis aus.
  onFindPlantsForMonth: PropTypes.func,
};

BloomPanel.defaultProps = {
  canvas: null,
  onFindPlantsForMonth: null,
};

export default BloomPanel;
